import React from 'react';
import { motion } from 'framer-motion';
import { Clock, ThumbsUp, MessageSquare, Calendar } from 'lucide-react';
import Button from '../../../components/ui/Button';

const TimelineView = ({ quarters, selectedQuarter, onQuarterSelect }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-500 bg-green-500/10';
      case 'in-progress': return 'text-blue-500 bg-blue-500/10';
      case 'planning': return 'text-yellow-500 bg-yellow-500/10';
      case 'planned': return 'text-purple-500 bg-purple-500/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in-progress': return 'In Progress';
      case 'planning': return 'Planning';
      case 'planned': return 'Planned';
      default: return 'Unknown';
    }
  };

  return (
    <div className="space-y-8">
      {/* Quarter Selector */}
      <div className="flex flex-wrap gap-3 justify-center">
        {quarters?.map((quarter) => (
          <Button
            key={quarter?.id}
            variant={selectedQuarter === quarter?.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => onQuarterSelect(quarter?.id)}
            className="flex items-center space-x-2"
          >
            <Calendar className="w-4 h-4" />
            <span>{quarter?.name}</span>
            {quarter?.status === 'active' && (
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            )}
          </Button>
        ))}
      </div>
      {/* Selected Quarter Details */}
      {quarters?.map((quarter) => (
        selectedQuarter === quarter?.id && (
          <motion.div
            key={quarter?.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-2">{quarter?.name} Development Plan</h2>
              <div className="flex items-center justify-center space-x-2">
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  quarter?.status === 'active' ? 'bg-green-500/10 text-green-500' : 'bg-muted/10 text-muted-foreground'
                }`}>
                  {quarter?.status === 'active' ? 'Currently Active' : 'Planned'}
                </div>
              </div>
            </div>

            <div className="grid gap-6">
              {quarter?.features?.map((feature, index) => (
                <motion.div
                  key={feature?.id}
                  className="bg-card border border-border rounded-xl p-6 hover:shadow-elevation-2 transition-all duration-300"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                    {/* Feature Info */}
                    <div className="flex-1 space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white">
                            {feature?.icon}
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold">{feature?.title}</h3>
                            <p className="text-sm text-muted-foreground">{feature?.category}</p>
                          </div>
                        </div>
                        
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(feature?.status)}`}>
                          {getStatusText(feature?.status)}
                        </div>
                      </div>

                      <p className="text-muted-foreground leading-relaxed">
                        {feature?.description}
                      </p>

                      {/* Progress Bar */}
                      {feature?.status === 'in-progress' && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span>Development Progress</span>
                            <span className="font-medium">{feature?.progress}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <motion.div 
                              className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full"
                              initial={{ width: 0 }}
                              animate={{ width: `${feature?.progress}%` }}
                              transition={{ duration: 1, delay: 0.5 }}
                            ></motion.div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Feature Stats */}
                    <div className="lg:w-64 space-y-4">
                      <div className="bg-muted/5 rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <ThumbsUp className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Community Votes</span>
                          </div>
                          <span className="font-semibold text-green-500">{feature?.votes}</span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <MessageSquare className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Comments</span>
                          </div>
                          <span className="font-semibold">{feature?.comments}</span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Est. Completion</span>
                          </div>
                          <span className="font-semibold text-sm">
                            {new Date(feature.estimatedCompletion)?.toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline" className="flex-1">
                          <ThumbsUp className="w-4 h-4 mr-1" />
                          Vote
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1">
                          <MessageSquare className="w-4 h-4 mr-1" />
                          Discuss
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Quarter Summary */}
            <div className="bg-muted/5 rounded-xl p-6 mt-8">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-500 mb-1">
                    {quarter?.features?.length || 0}
                  </div>
                  <div className="text-sm text-muted-foreground">Features Planned</div>
                </div>
                
                <div>
                  <div className="text-2xl font-bold text-green-500 mb-1">
                    {quarter?.features?.filter(f => f?.status === 'completed')?.length || 0}
                  </div>
                  <div className="text-sm text-muted-foreground">Completed</div>
                </div>
                
                <div>
                  <div className="text-2xl font-bold text-purple-500 mb-1">
                    {quarter?.features?.reduce((sum, f) => sum + f?.votes, 0) || 0}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Votes</div>
                </div>
              </div>
            </div>
          </motion.div>
        )
      ))}
    </div>
  );
};

export default TimelineView;